import React, {FC, useState} from "react"
import {FormattedMessage, FormattedNumber, useIntl} from "@irati/utils/lang"
import History, {historyContext} from "@irati/utils/history"
import {withTheme, WithDefaultTheme} from "styled-components"
import Flex from "@irati/ui/src/Flex"
import {H2, Label} from "@irati/ui/src/Typography"
import Button from "@irati/ui/src/Button"
import {Checkbox, ValidationButton} from "@irati/ui/src/FormItem"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import Validation from "@irati/utils/validation"
import {useBudgetsPermissions} from "@irati/wallet/hooks/budget"

import {DistributeRewardImportType, DistributeRewardRecipient} from "../../../enums/DistributeRewardTypes"
import {useRewardsDistribution, useRewardsDistributionActions} from "../../../stores/DistributeReward/hooks"
import TemplatePreview from "../../TemplatePreview"
import BudgetSummary from "./BudgetSummary"
import BudgetAvailableBalance from "./BudgetAvailableBalance"
import Loading from "@irati/ui/src/Loading"

interface Props extends WithDefaultTheme {}

const DistributeRewardSummaryStep2: FC<Props> = (_, context) => {
  const state = useRewardsDistribution()
  const {formatMessage} = useIntl()
  const {category, unassigned, checkUnassigned, ...payload} = state
  const {sendManualReward, confirmBulkReward, dispatch, confirmBulkManagersCSV} = useRewardsDistributionActions()
  const [isChecked, setIsChecked] = useState<boolean>(false)
  const [submit, setSubmit] = useState<Promise<any> | null>(null)
  const {canBudget} = useBudgetsPermissions()

  const confirmSendReward = () => {
    setIsChecked((current) => !current)
  }

  const handleSubmit = () => {
    if (payload.rewardImportType === DistributeRewardImportType.IMPORT_CSV) {
      if (payload.recipientType === DistributeRewardRecipient.MANAGER) {
        setSubmit(confirmBulkManagersCSV(state))
        return
      }
      setSubmit(confirmBulkReward(state))
      return
    }
    setSubmit(sendManualReward(state).then(() => History.push("/flexi-reward", context)))
  }

  const totUsers =
    payload.rewardImportType === DistributeRewardImportType.IMPORT_CSV ? payload.userEmails.length : payload.destinationUsers.length
  const totalToPay =
    payload.rewardImportType === DistributeRewardImportType.IMPORT_CSV && payload.recipientType === DistributeRewardRecipient.MANAGER
      ? payload.bulkBudgetAssignmentState.totalToPay
      : payload.pointsPerUser * totUsers || 0

  return (
    <>
      <TemplatePreview boxed />

      <BudgetSummary />
      <BudgetAvailableBalance />

      <Flex justifyContent="space-between" alignItems="center" padding={canBudget ? [0, 3, 3] : 3}>
        <Label large>
          <FormattedMessage id="wallet.reward.admin.card.topup.totalToPay" />
        </Label>
        <Flex alignItems="center">
          <Icon icon={IconTypes.rewardPoints} size={32} />
          <H2 marginLeft={1}>
            <FormattedNumber value={totalToPay} />
          </H2>
        </Flex>
      </Flex>
      <Flex paddingLeft={3} paddingRight={3}>
        <Checkbox
          name="rewardAccord"
          checked={isChecked}
          validation={Validation.bool().oneOf([true], formatMessage({id: "platform.defaultValidation.required"}))}
          onChange={confirmSendReward}>
          <FormattedMessage id="wallet.reward.admin.distribute.checkbox.title" />
        </Checkbox>
      </Flex>
      <Flex justifyContent="space-between" paddingLeft={3} paddingRight={3} paddingBottom={3}>
        {payload.step !== "recipients" && (
          <Button
            id="rewardWallet_distributeRewardStep2_back_button"
            onClick={() => dispatch({type: "SET_STEP", payload: "message"})}
            marginRight>
            <FormattedMessage id="platform.button.back" />
          </Button>
        )}
        <ValidationButton
          id={
            payload.recipientType === DistributeRewardRecipient.MANAGER
              ? "rewardWallet_distributeReward_assignReward_button"
              : "rewardWallet_distributeReward_sendReward_button"
          }
          full
          primary
          onClick={handleSubmit}
          load={submit}>
          <FormattedMessage
            id={
              payload.recipientType === DistributeRewardRecipient.MANAGER
                ? "wallet.reward.admin.btn.assign"
                : "wallet.reward.admin.btn.sendReward"
            }
          />
        </ValidationButton>
      </Flex>
      <Flex justifyContent="center" margin={[0, 2]} paddingBottom={2}>
        <Loading useLoader={false} load={submit} />
      </Flex>
    </>
  )
}

DistributeRewardSummaryStep2.contextTypes = historyContext

export default withTheme(DistributeRewardSummaryStep2)
